<template>
  <div>
    <div>
      <fin-category-dropdown v-model="selectedCategories"
                             :disabled="disabled"
                             :dropdown-height="(50 * Math.min(nbOfVisibleDropdownItems, categories.length) + 10) + 'px'"
                             :items="categories"
                             @input="val => doFilter(0, val)"/>

      <fin-archetype-dropdown v-model="selectedArchetypes"
                              :disabled="disabled"
                              :dropdown-height="(50 * Math.min(nbOfVisibleDropdownItems, archetypes.length) + 10) + 'px'"
                              :highlight-archetype-id="userArchetype ? userArchetype.id : null"
                              :items="archetypes"
                              highlight-text="Mein Archetyp"
                              highlight-as-first
                              @input="val => doFilter(1, val)"/>

      <!-- asset classes -->
      <fin-dropdown v-model="selectedSimpleFilter"
                    :disabled="disabled"
                    :dropdown-height="(50 * Math.min(nbOfVisibleDropdownItems, simpleFilter.length) + 10) + 'px'"
                    :items="simpleFilter"
                    :label="simpleFilterLabel"
                    hide-all-selection
                    show-selection-badge
                    check-attribute="value"
                    @input="val => doFilter(2, val)">
        <template #default="{item}">
          {{ item.title }}
        </template>
      </fin-dropdown>
    </div>

    <div :style="'min-height: 32px;' + disabled ? 'opacity: 0.4;' : ''" class="mb-3">
      <span v-if="showFilter" class="mr-2" style="font-weight: bold;">Aktive Filter: </span>

      <span v-if="showCategoryFilter">
        <fin-tag v-for="filter in selectedCategories"
                 :key="filter.id" class="mr-2"
                 style="display: inline-block;">
          {{ filter.name }}
          <v-btn v-if="assetClassId === undefined"
                 icon
                 x-small
                 @click="removeFilter(0, filter)">
            <v-icon>icon-close-small</v-icon>
          </v-btn>
        </fin-tag>
      </span>

      <span v-if="showArchetypeFilter">
        <fin-tag v-for="filter in selectedArchetypes"
                 :key="filter.id"
                 class="mr-2"
                 style="display: inline-block;">
          {{ filter.title }}
          <v-btn v-if="assetClassId === undefined" icon x-small @click="removeFilter(1, filter)">
            <v-icon>icon-close-small</v-icon>
          </v-btn>
        </fin-tag>
      </span>

      <span v-if="showSimpleFilter">
        <fin-tag v-for="filter in selectedSimpleFilter"
                 :key="filter.id"
                 class="mr-2"
                 style="display: inline-block;">
          {{ filter.title }}
          <v-btn v-if="assetClassId === undefined" icon x-small @click="removeFilter(2, filter)">
            <v-icon>icon-close-small</v-icon>
          </v-btn>
        </fin-tag>
      </span>
    </div>
  </div>
</template>

<script>
import FinDropdown from "@/components/common/Dropdown"
import FinCategoryDropdown from "@/components/common/DataDropdowns/CategoryDropdown"
import FinArchetypeDropdown from "@/components/common/DataDropdowns/ArchetypeDropdown"

export default {
  name: "finStrategyFilter",
  components: {FinArchetypeDropdown, FinCategoryDropdown, FinDropdown},
  props: {
    filterableItems: {
      type: Array,
      default: () => []
    },
    /** items to display in the categories dropdown */
    categories: {
      type: Array,
      default: () => []
    },
    /** items to display in the archetypes dropdown */
    archetypes: {
      type: Array,
      default: () => []
    },
    userArchetype: {
      type: Object
    },
    simpleFilterLabel: {
      type: String
    },
    /** items to display in the asset classes dropdown */
    simpleFilter: {
      type: Array,
      default: () => []
    },
    simpleFilterItemsName: {
      type: String
    },
    assetClassId: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      selectedCategories: [],
      selectedArchetypes: [],
      selectedSimpleFilter: [],
      nbOfVisibleDropdownItems: 4
    }
  },
  computed: {
    showCategoryFilter() {
      return this.selectedCategories.length > 0
    },
    showArchetypeFilter() {
      return this.selectedArchetypes.length > 0
    },
    showSimpleFilter() {
      return this.selectedSimpleFilter.length > 0
    },
    showFilter() {
      return this.showCategoryFilter || this.showArchetypeFilter || this.showSimpleFilter
    },
    disabled() {
      return this.assetClassId !== undefined && this.assetClassId !== 'SHARE'
    }
  },
  methods: {
    doFilter(index, filteredItems) {
      // categories
      if (index === 0) {
        // reset other filters
        this.selectedArchetypes = []
        this.selectedSimpleFilter = []

        this.selectedCategories = [...filteredItems]
        this.filterCategories()
      }
      // archetypes
      else if (index === 1) {
        // reset other filters
        this.selectedCategories = []
        this.selectedSimpleFilter = []

        this.selectedArchetypes = [...filteredItems]
        this.filterArchetypes()
      }
      // simple filter
      else if (index === 2) {
        // reset other filters
        this.selectedCategories = []
        this.selectedArchetypes = []

        this.selectedSimpleFilter = [...filteredItems]
        this.filterSimpleFilterItems()
      }
    },
    removeFilter(index, removeFilter) {
      // categories
      if (index === 0) {
        this.selectedCategories = this.selectedCategories.filter(c => c.id !== removeFilter.id)
        this.filterCategories()
      }
      // archetypes
      else if (index === 1) {
        this.selectedArchetypes = this.selectedArchetypes.filter(a => a.id !== removeFilter.id)
        this.filterArchetypes()
      }
      // simple filter
      else if (index === 2) {
        this.selectedSimpleFilter = this.selectedSimpleFilter.filter(r => r.value !== removeFilter.value)
        this.filterSimpleFilterItems()
      }
    },
    filterCategories() {
      if (this.selectedCategories.length > 0) {
        const filtered = this.filterableItems.filter(card => {
          if (card.category && this.selectedCategories.find(s => s.id === card.category.id)) {
            return true
          }
          return false
        })

        this.$emit('itemsChanged', filtered)
      } else {
        this.$emit('itemsChanged', [...this.filterableItems])
      }
    },
    filterArchetypes() {
      if (this.selectedArchetypes.length > 0) {
        const filtered = this.filterableItems.filter(card => {
          if (card.archetypes && this.selectedArchetypes.find(s => card.archetypes.items.find(c => c.archetypeID === s.id))) {
            return true
          }
          return false
        })

        this.$emit('itemsChanged', filtered)
      } else {
        this.$emit('itemsChanged', [...this.filterableItems])
      }
    },
    filterSimpleFilterItems() {
      if (this.selectedSimpleFilter.length > 0) {
        const filtered = this.filterableItems.filter(card => {
          if (card[this.simpleFilterItemsName] && this.selectedSimpleFilter.find(s => card[this.simpleFilterItemsName].indexOf(s.value) >= 0)) {
            return true
          }
          return false
        })

        this.$emit('itemsChanged', filtered)
      } else {
        this.$emit('itemsChanged', [...this.filterableItems])
      }
    }
  },
  mounted() {
    // if an assetClassId is set only show items belonging to this assetClass
    if (this.assetClassId) {
      let selectedAssetClassItem = this.simpleFilter.find(item => {
        return item.value === this.assetClassId
      })

      if (selectedAssetClassItem) {
        this.selectedSimpleFilter = [selectedAssetClassItem]
        this.filterSimpleFilterItems()
      }
    }
  }
}
</script>

<style scoped>

</style>
